import * as THREE from 'three';
import type { SceneryDensity, TrackDefinition, TrackTheme } from '../core/types';

export interface EnvironmentHandle {
  root: THREE.Group;
  update(elapsed: number, speed: number): void;
  dispose(): void;
}

class Random {
  constructor(private state: number) {}

  next(): number {
    this.state = Math.imul(this.state ^ (this.state >>> 15), 1 | this.state);
    this.state ^= this.state + Math.imul(this.state ^ (this.state >>> 7), 61 | this.state);
    return ((this.state ^ (this.state >>> 14)) >>> 0) / 4_294_967_296;
  }

  range(min: number, max: number): number {
    return min + (max - min) * this.next();
  }
}

function hash(value: string): number {
  let result = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 16777619);
  }
  return result >>> 0;
}

function material(color: number, roughness = 0.8, metalness = 0.05, emissive?: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    roughness,
    metalness,
    emissive: emissive ?? 0x000000,
    emissiveIntensity: emissive === undefined ? 0 : 1.5,
  });
}

function createSkyDome(track: TrackDefinition): THREE.Mesh {
  const top = new THREE.Color(track.palette.sky).offsetHSL(0.01, 0.08, track.theme === 'neon' ? -0.09 : 0.11);
  const bottom = new THREE.Color(track.palette.fog).offsetHSL(0, -0.05, track.theme === 'sky' ? 0.13 : 0.02);
  const geometry = new THREE.SphereGeometry(1600, 28, 14);
  const shader = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    uniforms: {
      topColor: { value: top },
      bottomColor: { value: bottom },
      offset: { value: 18 },
      exponent: { value: track.theme === 'neon' ? 0.9 : 0.58 },
    },
    vertexShader: `
      varying vec3 vWorldPosition;
      void main() {
        vec4 worldPosition = modelMatrix * vec4(position, 1.0);
        vWorldPosition = worldPosition.xyz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 topColor;
      uniform vec3 bottomColor;
      uniform float offset;
      uniform float exponent;
      varying vec3 vWorldPosition;
      void main() {
        float h = normalize(vWorldPosition + vec3(0.0, offset, 0.0)).y;
        gl_FragColor = vec4(mix(bottomColor, topColor, max(pow(max(h, 0.0), exponent), 0.0)), 1.0);
      }
    `,
  });
  return new THREE.Mesh(geometry, shader);
}

function isFarFromTrack(x: number, z: number, points: THREE.Vector3[], clearance: number): boolean {
  let closest = Number.POSITIVE_INFINITY;
  const stride = Math.max(1, Math.floor(points.length / 120));
  for (let index = 0; index < points.length; index += stride) {
    const dx = points[index].x - x;
    const dz = points[index].z - z;
    closest = Math.min(closest, dx * dx + dz * dz);
    if (closest < clearance * clearance) return false;
  }
  return true;
}

function getBounds(points: THREE.Vector3[]): THREE.Box3 {
  const bounds = new THREE.Box3().setFromPoints(points);
  if (bounds.isEmpty()) bounds.setFromCenterAndSize(new THREE.Vector3(), new THREE.Vector3(500, 50, 500));
  bounds.min.x -= 120;
  bounds.max.x += 120;
  bounds.min.z -= 120;
  bounds.max.z += 120;
  return bounds;
}

function createGround(track: TrackDefinition, bounds: THREE.Box3): THREE.Group {
  const root = new THREE.Group();
  const size = Math.max(bounds.max.x - bounds.min.x, bounds.max.z - bounds.min.z, 900) * 1.6;
  if (track.theme === 'sky') return root;

  const groundOffset = track.theme === 'desert' ? 4.5 : track.theme === 'gauntlet' ? 5.5 : 1.8;
  const groundY = bounds.min.y - groundOffset;
  const groundMaterial = material(track.palette.ground, 0.96, track.theme === 'neon' ? 0.2 : 0.02);
  const ground = new THREE.Mesh(new THREE.PlaneGeometry(size, size, 64, 64), groundMaterial);
  const positions = ground.geometry.getAttribute('position');
  const random = new Random(hash(track.id) + 77);
  for (let index = 0; index < positions.count; index += 1) {
    const x = positions.getX(index);
    const y = positions.getY(index);
    const edge = Math.min(1, Math.hypot(x, y) / (size * 0.34));
    positions.setZ(index, (random.next() - 0.5) * (track.theme === 'desert' ? 7 : 3) * edge);
  }
  ground.geometry.computeVertexNormals();
  ground.rotation.x = -Math.PI / 2;
  ground.position.set(bounds.getCenter(new THREE.Vector3()).x, groundY, bounds.getCenter(new THREE.Vector3()).z);
  ground.receiveShadow = true;
  root.add(ground);

  if (track.theme === 'coast') {
    const water = new THREE.Mesh(
      new THREE.PlaneGeometry(size * 1.4, size * 1.4),
      new THREE.MeshPhysicalMaterial({
        color: 0x083b5e,
        roughness: 0.16,
        metalness: 0.35,
        transparent: true,
        opacity: 0.85,
      }),
    );
    water.rotation.x = -Math.PI / 2;
    water.position.copy(ground.position).setY(groundY - 1.5);
    root.add(water);
  }
  return root;
}

function createPine(random: Random, scale: number): THREE.Group {
  const group = new THREE.Group();
  const trunkMaterial = material(0x4b2f21, 0.95);
  const leafMaterial = material(random.next() > 0.5 ? 0x173f32 : 0x205440, 0.9);
  const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.3 * scale, 0.5 * scale, 3.3 * scale, 6), trunkMaterial);
  trunk.position.y = 1.65 * scale;
  trunk.castShadow = true;
  group.add(trunk);
  for (let tier = 0; tier < 3; tier += 1) {
    const cone = new THREE.Mesh(
      new THREE.ConeGeometry((2.2 - tier * 0.4) * scale, 3.6 * scale, 7),
      leafMaterial,
    );
    cone.position.y = (3.4 + tier * 1.55) * scale;
    cone.castShadow = true;
    group.add(cone);
  }
  return group;
}

function createRock(random: Random, color: number, scale: number): THREE.Mesh {
  const geometry = new THREE.DodecahedronGeometry(scale, 0);
  const positions = geometry.getAttribute('position');
  for (let index = 0; index < positions.count; index += 1) {
    const factor = random.range(0.78, 1.22);
    positions.setXYZ(index, positions.getX(index) * factor, positions.getY(index) * random.range(0.7, 1.4), positions.getZ(index) * factor);
  }
  geometry.computeVertexNormals();
  const mesh = new THREE.Mesh(geometry, material(color, 0.94));
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function createBuilding(random: Random, track: TrackDefinition): THREE.Group {
  const group = new THREE.Group();
  const width = random.range(8, 22);
  const depth = random.range(8, 20);
  const height = random.range(18, 75);
  const shell = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), material(0x101427, 0.46, 0.55));
  shell.position.y = height / 2;
  shell.castShadow = true;
  shell.receiveShadow = true;
  group.add(shell);
  const windowMaterial = new THREE.MeshStandardMaterial({
    color: track.palette.accent,
    emissive: random.next() > 0.45 ? track.palette.accent : track.palette.secondary,
    emissiveIntensity: 3.4,
    roughness: 0.3,
  });
  const strips = Math.max(2, Math.floor(height / 8));
  for (let index = 0; index < strips; index += 1) {
    const strip = new THREE.Mesh(new THREE.BoxGeometry(width * 0.72, 0.35, depth + 0.04), windowMaterial);
    strip.position.y = 4 + index * ((height - 6) / strips);
    group.add(strip);
  }
  const antenna = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, random.range(4, 11), 5), windowMaterial);
  antenna.position.y = height + 3;
  group.add(antenna);
  return group;
}

function createCloud(random: Random, scale: number): THREE.Group {
  const group = new THREE.Group();
  const cloudMaterial = new THREE.MeshStandardMaterial({ color: 0xe8f4ff, roughness: 1, transparent: true, opacity: 0.72, depthWrite: false });
  for (let index = 0; index < 5; index += 1) {
    const puff = new THREE.Mesh(new THREE.DodecahedronGeometry(random.range(6, 13) * scale, 1), cloudMaterial);
    puff.scale.y = random.range(0.35, 0.7);
    puff.position.set((index - 2) * random.range(5, 9) * scale, random.range(-2, 3) * scale, random.range(-4, 4) * scale);
    group.add(puff);
  }
  return group;
}

function densityCount(density: SceneryDensity, low: number, medium: number, high: number): number {
  return density === 'low' ? low : density === 'medium' ? medium : high;
}

function addSupports(root: THREE.Group, track: TrackDefinition, points: THREE.Vector3[]): void {
  if (track.theme !== 'sky') return;
  const supportMaterial = material(0x344964, 0.38, 0.75);
  const accentMaterial = material(track.palette.accent, 0.22, 0.55, track.palette.accent);
  const stride = Math.max(10, Math.floor(points.length / 28));
  for (let index = 0; index < points.length; index += stride) {
    const point = points[index];
    const height = Math.max(16, point.y + 70);
    const column = new THREE.Mesh(new THREE.CylinderGeometry(0.8, 2.4, height, 6), supportMaterial);
    column.position.set(point.x, point.y - height / 2 - 1, point.z);
    column.castShadow = true;
    root.add(column);
    const ring = new THREE.Mesh(new THREE.TorusGeometry(3, 0.18, 6, 16), accentMaterial);
    ring.position.set(point.x, point.y - 4, point.z);
    ring.rotation.x = Math.PI / 2;
    root.add(ring);
  }
}

export function configureTrackLighting(scene: THREE.Scene, track: TrackDefinition): THREE.Group {
  const root = new THREE.Group();
  scene.background = new THREE.Color(track.palette.sky);
  const fogDensity = track.theme === 'neon' ? 0.0015 : track.theme === 'sky' ? 0.0007 : 0.00105;
  scene.fog = new THREE.FogExp2(track.palette.fog, fogDensity);

  const hemisphere = new THREE.HemisphereLight(
    track.theme === 'neon' ? 0x7088c8 : 0xcde7ff,
    track.palette.ground,
    track.theme === 'neon' ? 1.25 : 1.75,
  );
  root.add(hemisphere);
  const sun = new THREE.DirectionalLight(track.theme === 'neon' ? 0x9bb4ff : 0xfff0cf, track.theme === 'neon' ? 2.2 : 3.8);
  sun.position.set(-120, 180, 80);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  sun.shadow.camera.near = 1;
  sun.shadow.camera.far = 600;
  sun.shadow.camera.left = -130;
  sun.shadow.camera.right = 130;
  sun.shadow.camera.top = 130;
  sun.shadow.camera.bottom = -130;
  sun.shadow.bias = -0.00015;
  root.add(sun);
  if (track.theme === 'neon' || track.theme === 'gauntlet') {
    const fill = new THREE.DirectionalLight(track.palette.accent, 1.4);
    fill.position.set(120, 60, -120);
    root.add(fill);
  }
  scene.add(root);
  return root;
}

export function createEnvironment(
  track: TrackDefinition,
  density: SceneryDensity,
  pathPoints: THREE.Vector3[],
): EnvironmentHandle {
  const root = new THREE.Group();
  root.name = `environment-${track.id}`;
  const random = new Random(hash(track.id));
  const bounds = getBounds(pathPoints);
  root.add(createSkyDome(track), createGround(track, bounds));
  addSupports(root, track, pathPoints);

  const center = bounds.getCenter(new THREE.Vector3());
  const width = bounds.max.x - bounds.min.x;
  const depth = bounds.max.z - bounds.min.z;
  const yBase = track.theme === 'sky'
    ? Math.min(...pathPoints.map((point) => point.y)) - 45
    : bounds.min.y - (track.theme === 'desert' ? 4 : track.theme === 'gauntlet' ? 5 : 1.4);
  const objectCount = densityCount(density, 34, 66, 105);
  const animated: THREE.Object3D[] = [];

  for (let index = 0; index < objectCount; index += 1) {
    let x = center.x;
    let z = center.z;
    let attempts = 0;
    do {
      x = center.x + random.range(-width * 0.57, width * 0.57);
      z = center.z + random.range(-depth * 0.57, depth * 0.57);
      attempts += 1;
    } while (!isFarFromTrack(x, z, pathPoints, track.roadWidth * 1.65) && attempts < 12);

    let object: THREE.Object3D;
    if (track.theme === 'forest') {
      object = random.next() > 0.22 ? createPine(random, random.range(0.72, 1.55)) : createRock(random, 0x5f645d, random.range(2, 6));
    } else if (track.theme === 'neon') {
      object = createBuilding(random, track);
    } else if (track.theme === 'desert') {
      object = createRock(random, random.next() > 0.45 ? 0xa84d2d : 0xd16f3b, random.range(3, 12));
      object.scale.y = random.range(1.2, 3.2);
    } else if (track.theme === 'sky') {
      object = createCloud(random, random.range(0.65, 1.45));
      animated.push(object);
    } else if (track.theme === 'gauntlet') {
      object = random.next() > 0.4 ? createRock(random, 0x251c2d, random.range(4, 13)) : createBuilding(random, track);
    } else {
      object = random.next() > 0.42 ? createRock(random, 0x557078, random.range(2, 7)) : createPine(random, random.range(0.65, 1.1));
    }
    const baseHeight = track.theme === 'sky' ? yBase + random.range(-35, 30) : yBase;
    object.position.set(x, baseHeight, z);
    object.rotation.y = random.range(0, Math.PI * 2);
    object.userData.phase = random.range(0, Math.PI * 2);
    object.userData.drift = random.range(0.4, 1.25);
    root.add(object);
  }

  return {
    root,
    update(elapsed, speed) {
      for (const object of animated) {
        object.position.x += Math.sin(elapsed * 0.05 + object.userData.phase) * object.userData.drift * 0.006 * Math.min(2, 1 + speed / 80);
        object.position.y += Math.sin(elapsed * 0.18 + object.userData.phase) * 0.003;
      }
    },
    dispose() {
      root.traverse((object) => {
        if (object instanceof THREE.Mesh) {
          object.geometry.dispose();
          const materials = Array.isArray(object.material) ? object.material : [object.material];
          materials.forEach((item) => item.dispose());
        }
      });
      root.removeFromParent();
    },
  };
}

export function getThemeLabel(theme: TrackTheme): string {
  return ({
    coast: 'Coastal proving ground',
    forest: 'Mountain forest',
    neon: 'Midnight megacity',
    desert: 'Desert canyon',
    sky: 'Suspended skyway',
    gauntlet: 'Industrial endgame',
  } satisfies Record<TrackTheme, string>)[theme];
}
